import React, { useState } from 'react';
import './Auth.css';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const Signup = () => {
    const { signup, loginWithGoogle } = useAuth();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        userType: 'Individual',
        vendorName: '',
        slug: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            return setError('Passwords do not match');
        }

        if (!agreedToTerms) {
            return setError('You must agree to the Terms & Conditions and Privacy Policy to register.');
        }

        if (formData.userType === 'Corporate' && !formData.slug.trim()) {
            return setError('Profile Slug is required for Corporate accounts');
        }

        try {
            setLoading(true);
            await signup(
                formData.email,
                formData.password,
                formData.fullName,
                formData.phone,
                formData.userType,
                formData.vendorName,
                formData.slug
            );
            navigate('/');
        } catch (err) {
            setError('Failed to create an account. ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignup = async () => {
        if (!agreedToTerms) {
            return setError('You must agree to the Terms & Conditions and Privacy Policy to register.');
        }

        try {
            setError('');
            setLoading(true);
            await loginWithGoogle();
            navigate('/');
        } catch (err) {
            setError('Google Sign up failed. ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <h2 className="section-title">Sign Up</h2>
            <form onSubmit={handleSubmit} className="auth-form">
                {error && <div className="error-message">{error}</div>}

                {/* User Type Selection */}
                <div className="form-group">
                    <label>Account Type</label>
                    <div className="user-type-selector">
                        <button
                            type="button"
                            className={`type-option ${formData.userType === 'Individual' ? 'active' : ''}`}
                            onClick={() => setFormData({ ...formData, userType: 'Individual' })}
                        >
                            <i className="fa-solid fa-user"></i>
                            <span>Individual</span>
                        </button>
                        <button
                            type="button"
                            className={`type-option ${formData.userType === 'Corporate' ? 'active' : ''}`}
                            onClick={() => setFormData({ ...formData, userType: 'Corporate' })}
                        >
                            <i className="fa-solid fa-building"></i>
                            <span>Corporate</span>
                        </button>
                    </div>
                </div>

                <div className="form-group">
                    <label>Full Name</label>
                    <input type="text" name="fullName" required onChange={handleChange} />
                </div>

                {formData.userType === 'Corporate' && (
                    <>
                        <div className="form-group">
                            <label>Vendor / Business Name</label>
                            <input
                                type="text"
                                name="vendorName"
                                placeholder="e.g. ABC Motors"
                                required
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label>Profile Slug (e.g. abc-motors)</label>
                            <input
                                type="text"
                                name="slug"
                                placeholder="e.g. abc-motors (no spaces)"
                                required
                                onChange={(e) => setFormData({ ...formData, slug: e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase() })}
                                value={formData.slug}
                            />
                        </div>
                    </>
                )}

                <div className="form-group">
                    <label>Email</label>
                    <input type="email" name="email" required onChange={handleChange} />
                </div>
                <div className="form-group">
                    <label>Phone Number</label>
                    <input type="tel" name="phone" required onChange={handleChange} />
                </div>
                <div className="form-group">
                    <label>Password</label>
                    <input type="password" name="password" required onChange={handleChange} />
                </div>
                <div className="form-group">
                    <label>Confirm Password</label>
                    <input type="password" name="confirmPassword" required onChange={handleChange} />
                </div>

                <div className="form-group terms-checkbox-group">
                    <label className="terms-checkbox-label">
                        <input
                            type="checkbox"
                            checked={agreedToTerms}
                            onChange={(e) => setAgreedToTerms(e.target.checked)}
                        />
                        <span>
                            I agree to the{' '}
                            <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
                            {' '}and{' '}
                            <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                        </span>
                    </label>
                </div>

                <button disabled={loading || !agreedToTerms} type="submit" className="btn-submit">
                    Sign Up with Email
                </button>
            </form>

            <div className="social-divider">OR</div>

            <div className="social-login">
                <button
                    type="button"
                    className="btn-google"
                    onClick={handleGoogleSignup}
                    disabled={loading || !agreedToTerms}
                >
                    <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
                    Sign up with Google
                </button>
            </div>
            <div className="auth-footer">
                Already have an account? <Link to="/login">Log In</Link>
            </div>
        </div>
    );
};

export default Signup;
